// web/src/screens/admin/tts_phrases.ts
//
// /admin/tts — the TTS console: (1) active voice & Google-steering settings,
// (2) the voice catalogue (add / delete / set-active), and (3) the in-use
// phrase / audio browser for the selected voice, with per-row rebuild,
// per-phrase steering overrides, and bulk "rebuild missing / all".
//
// v2-aligned rebuild of v1's /admin/tts/voices. The voice catalogue is the
// `voice` DB table; the active-voice picker is /api/config/tts; audio is
// content-addressed and previewed via /storage/tts/<sha>.<ext>?v=<mtime>.
//
// All API-supplied text via textContent only (T-11-09a).

import { renderAdminShell, showAdminToast, TTS_COVERAGE_INVALIDATE_EVENT } from "./admin_shell.js";
import { AdminError } from "../../api/admin.js";
import {
  getTTSVoices, createTTSVoice, deleteTTSVoice, getTTSPhrases,
  rebuildTTSBulk, setTTSOverride, deleteTTSOverride, rebuildTextAudio,
  updatePhraseContent,
} from "../../api/admin.js";
import { getTTSConfig, setTTSChoice, setTTSGoogle, TTSConfigError } from "../../api/config.js";
import type {
  AdminRouteProps, AdminVoiceDTO, AdminTTSPhraseDTO, AdminTTSPhraseListDTO,
  AdminPhraseOverrideUpsertDTO, TTSConfig,
} from "../../types/api.js";
import {
  section, table, pill, code, adminLink, navigateAdmin, errMessage,
} from "./logs_common.js";
import { openModal } from "./modal.js";

const PHRASE_COLS = 5; // short_code, Content, Source keys, Audio, Actions

// LabeledField is a {label, input} pair returned by the mk* form helpers.
type LabeledField<T extends HTMLElement> = { label: HTMLLabelElement; input: T };

export function mount(root: HTMLElement, _props?: unknown): () => void {
  void (_props as AdminRouteProps | undefined); // route props carry no fields

  // Breadcrumb shows the path *within* the TTS section only — the top nav
  // (renderAdminShell) already handles cross-section navigation, so we don't
  // prefix with Games/Game-log here. /admin/tts is the section landing, so
  // the breadcrumb is just "TTS"; the Phrases / Cost & calls sub-nav below
  // is the within-section tab switcher.
  const main = renderAdminShell(root, [{ label: "TTS" }]);

  // Sub-nav: Phrases (current) · Cost & calls →
  const subnav = document.createElement("p");
  subnav.setAttribute("style", "margin: 0 0 16px 0; font-size: 14px; display: flex; gap: 16px;");
  const cur = document.createElement("strong");
  cur.textContent = "Phrases";
  subnav.appendChild(cur);
  subnav.appendChild(adminLink("Cost & calls →", "/admin/tts/logs"));
  main.appendChild(subnav);

  // Read the selected voice + page from the query string (so pagination and
  // voice changes are deep-linkable / Back-friendly, like sessions_list).
  const params = new URLSearchParams(window.location.search);
  const selectedVoiceID = params.get("voice") ?? "";
  let page = 1;
  { const p = parseInt(params.get("page") ?? "1", 10); if (Number.isFinite(p) && p > 0) page = p; }

  // ── State assembled from the two voice sources ────────────────────────
  let cfg: TTSConfig | null = null;            // /api/config/tts (active + rendered flags)
  let voices: AdminVoiceDTO[] = [];            // /api/admin/tts/voices (all, incl. deleted)
  const renderedByTuple = new Map<string, boolean>(); // `${provider}|${voice}` → available

  // ── Section 1: active voice & steering ───────────────────────────────
  const settingsBody = section(main, "Active voice & steering");
  const activeRow = document.createElement("div");
  activeRow.setAttribute("style", "display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 8px;");
  const activeLabel = document.createElement("label");
  activeLabel.textContent = "Active voice (kid app): ";
  activeLabel.setAttribute("style", "font-size: 14px; color: #374151;");
  const activeSel = document.createElement("select");
  activeSel.setAttribute("style", "font-size: 14px; padding: 4px 8px; border: 1px solid #d1d5db; border-radius: 4px;");
  activeLabel.appendChild(activeSel);
  activeRow.appendChild(activeLabel);
  const activeStatus = document.createElement("span");
  activeStatus.setAttribute("style", "font-size: 13px; color: #6b7280;");
  activeStatus.setAttribute("aria-live", "polite");
  activeRow.appendChild(activeStatus);
  settingsBody.appendChild(activeRow);

  const steeringWrap = document.createElement("div");
  steeringWrap.setAttribute("style", "margin-top: 8px;");
  settingsBody.appendChild(steeringWrap);

  const hint = document.createElement("p");
  hint.textContent = "Switching to a pre-rendered voice takes effect immediately. New voices need a `make ttsbuild` pass (or use “Rebuild all” below for the selected voice) before they’re selectable as the active voice.";
  hint.setAttribute("style", "font-size: 12px; color: #6b7280; margin: 8px 0 0 0;");
  settingsBody.appendChild(hint);

  // ── Section 2: voice catalogue ───────────────────────────────────────
  const catalogBody = section(main, "Voices");
  const catRow = document.createElement("div");
  catRow.setAttribute("style", "display: flex; gap: 8px; align-items: center; flex-wrap: wrap;");
  const catLabel = document.createElement("label");
  catLabel.textContent = "Inspect / rebuild voice: ";
  catLabel.setAttribute("style", "font-size: 14px; color: #374151;");
  const catSel = document.createElement("select");
  catSel.setAttribute("style", "font-size: 14px; padding: 4px 8px; border: 1px solid #d1d5db; border-radius: 4px;");
  catLabel.appendChild(catSel);
  catRow.appendChild(catLabel);
  const setActiveBtn = mkBtn("Set as active", "primary");
  const deleteVoiceBtn = mkBtn("Delete voice", "danger");
  const addVoiceBtn = mkBtn("Add voice…", "outline");
  catRow.appendChild(setActiveBtn);
  catRow.appendChild(deleteVoiceBtn);
  catRow.appendChild(addVoiceBtn);
  catalogBody.appendChild(catRow);
  const catStatus = document.createElement("p");
  catStatus.setAttribute("style", "font-size: 13px; color: #6b7280; margin: 8px 0 0 0; min-height: 1.2em;");
  catStatus.setAttribute("aria-live", "polite");
  catalogBody.appendChild(catStatus);
  const addVoiceForm = document.createElement("div"); // populated lazily
  addVoiceForm.style.display = "none";
  catalogBody.appendChild(addVoiceForm);

  // ── Section 3: in-use phrases ────────────────────────────────────────
  const phrasesBody = section(main, "In-use phrases");
  const phrasesStatus = document.createElement("p");
  phrasesStatus.textContent = "Loading…";
  phrasesStatus.setAttribute("style", "font-size: 14px; color: #6b7280; margin: 0 0 8px 0;");
  phrasesBody.appendChild(phrasesStatus);
  const bulkRow = document.createElement("div");
  bulkRow.setAttribute("style", "display: flex; gap: 8px; align-items: center; margin-bottom: 12px;");
  const rebuildMissingBtn = mkBtn("↻ Build missing", "primary");
  const rebuildAllBtn = mkBtn("↻ Rebuild all", "outline");
  const rebuildMissingIdleLabel = "↻ Build missing";
  const rebuildAllIdleLabel = "↻ Rebuild all";
  bulkRow.appendChild(rebuildMissingBtn);
  bulkRow.appendChild(rebuildAllBtn);
  const bulkNote = document.createElement("span");
  bulkNote.setAttribute("style", "font-size: 12px; color: #6b7280;");
  bulkRow.appendChild(bulkNote);
  phrasesBody.appendChild(bulkRow);
  const bulkResult = document.createElement("div");
  bulkResult.setAttribute("style", "margin-bottom: 12px;");
  phrasesBody.appendChild(bulkResult);
  const tableWrap = document.createElement("div");
  tableWrap.setAttribute("style", "overflow-x: auto;");
  phrasesBody.appendChild(tableWrap);
  const pager = document.createElement("div");
  pager.setAttribute("style", "display: flex; gap: 8px; align-items: center; margin-top: 12px; font-size: 14px;");
  phrasesBody.appendChild(pager);

  // ── Rendering ────────────────────────────────────────────────────────

  function setSettingsStatus(text: string, isErr = false): void {
    activeStatus.textContent = text;
    activeStatus.style.color = isErr ? "#991b1b" : "#6b7280";
  }
  function setCatStatus(text: string, isErr = false): void {
    catStatus.textContent = text;
    catStatus.style.color = isErr ? "#991b1b" : "#6b7280";
  }

  function tupleKey(provider: string, voice: string): string { return `${provider}|${voice}`; }

  function renderActiveSelect(): void {
    activeSel.replaceChildren();
    if (!cfg) return;
    for (const o of cfg.options) {
      const opt = document.createElement("option");
      opt.value = tupleKey(o.provider, o.voice);
      opt.textContent = o.available ? o.label : `${o.label} — vēl nav sagatavota`;
      opt.disabled = !o.available;
      activeSel.appendChild(opt);
    }
    activeSel.value = tupleKey(cfg.provider, cfg.voice);
  }

  function renderSteering(): void {
    steeringWrap.replaceChildren();
    if (!cfg || cfg.provider !== "google") return;
    const promptLabel = document.createElement("label");
    promptLabel.setAttribute("style", "display: block; font-size: 13px; color: #374151; margin-bottom: 4px;");
    promptLabel.textContent = "Google steering prompt:";
    const promptTa = document.createElement("textarea");
    promptTa.rows = 4;
    promptTa.maxLength = 2000;
    promptTa.value = cfg.google.prompt;
    promptTa.setAttribute("style", "width: 100%; max-width: 640px; font-size: 13px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; font-family: inherit;");
    promptLabel.appendChild(promptTa);
    steeringWrap.appendChild(promptLabel);
    const modelLabel = document.createElement("label");
    modelLabel.setAttribute("style", "display: block; font-size: 13px; color: #374151; margin: 8px 0 4px 0;");
    modelLabel.textContent = "Google model:";
    const modelInput = document.createElement("input");
    modelInput.type = "text";
    modelInput.value = cfg.google.model;
    modelInput.setAttribute("style", "width: 100%; max-width: 360px; font-size: 13px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px;");
    modelLabel.appendChild(modelInput);
    steeringWrap.appendChild(modelLabel);
    const saveBtn = mkBtn("Save steering", "primary");
    saveBtn.style.marginTop = "8px";
    saveBtn.addEventListener("click", () => {
      void (async () => {
        saveBtn.disabled = true;
        try {
          const saved = await setTTSGoogle({ prompt: promptTa.value, model: modelInput.value });
          promptTa.value = saved.prompt;
          modelInput.value = saved.model;
          if (cfg) { cfg.google = saved; }
          showAdminToast(main, "Steering saved");
        } catch (err) {
          setSettingsStatus(errMessage(err, "save steering"), true);
        } finally { saveBtn.disabled = false; }
      })();
    });
    steeringWrap.appendChild(saveBtn);
  }

  function activeVoice(): AdminVoiceDTO | undefined { return voices.find((v) => v.is_active); }
  function currentBrowserVoice(): AdminVoiceDTO | undefined {
    if (selectedVoiceID) return voices.find((v) => v.id === selectedVoiceID);
    return activeVoice() ?? voices.find((v) => !v.deleted);
  }

  function renderCatalogSelect(): void {
    catSel.replaceChildren();
    for (const v of voices) {
      const opt = document.createElement("option");
      opt.value = v.id;
      let suffix = "";
      if (v.deleted) suffix = " (deleted)";
      else if (v.is_active) suffix = " (active)";
      else if (renderedByTuple.get(tupleKey(v.provider, v.voice)) === false) suffix = " (not rendered)";
      opt.textContent = `${v.label}${suffix}`;
      catSel.appendChild(opt);
    }
    const cur = currentBrowserVoice();
    if (cur) catSel.value = cur.id;
    // Enable/disable the action buttons for the selected catalogue voice.
    const sel = voices.find((v) => v.id === catSel.value);
    const rendered = sel ? renderedByTuple.get(tupleKey(sel.provider, sel.voice)) !== false : false;
    setActiveBtn.disabled = !sel || sel.deleted || sel.is_active || !rendered;
    deleteVoiceBtn.disabled = !sel || sel.deleted || sel.is_active;
  }

  // ── Data loading ─────────────────────────────────────────────────────

  async function loadSettingsAndCatalog(): Promise<void> {
    try {
      const [c, vl] = await Promise.all([getTTSConfig(), getTTSVoices()]);
      cfg = c;
      voices = vl.items;
      renderedByTuple.clear();
      for (const o of c.options) renderedByTuple.set(tupleKey(o.provider, o.voice), o.available);
      renderActiveSelect();
      renderSteering();
      renderCatalogSelect();
      setSettingsStatus("");
      setCatStatus("");
    } catch (err) {
      setSettingsStatus(errMessage(err, "load TTS settings"), true);
      setCatStatus(errMessage(err, "load voices"), true);
    }
  }

  async function loadPhrases(): Promise<void> {
    const v = currentBrowserVoice();
    if (!v) {
      phrasesStatus.textContent = "No voices in the catalogue — add one above.";
      tableWrap.replaceChildren();
      pager.replaceChildren();
      return;
    }
    phrasesStatus.textContent = `Loading phrases for ${v.label}…`;
    try {
      const list = await getTTSPhrases(v.id, page);
      renderPhrases(list);
    } catch (err) {
      phrasesStatus.textContent = errMessage(err, "load phrases");
      tableWrap.replaceChildren();
      pager.replaceChildren();
    }
  }

  function renderPhrases(list: AdminTTSPhraseListDTO): void {
    const from = list.total_phrases === 0 ? 0 : (list.page - 1) * list.page_size + 1;
    const to = Math.min(list.page * list.page_size, list.total_phrases);
    phrasesStatus.textContent = `${list.total_phrases} phrase(s) for ${list.voice.label} — showing ${from}–${to}`;

    const t = table(["Short code", "Content", "Source keys", "Audio", "Actions"]);
    for (const row of list.items) {
      const contentCell = document.createElement("span");
      const txt = document.createElement("span");
      txt.textContent = row.content;
      contentCell.appendChild(txt);
      if (row.has_override) {
        const p = pill("override", "#92400e", "#fef3c7");
        p.style.marginLeft = "6px";
        contentCell.appendChild(p);
      }
      const sources = document.createElement("small");
      sources.setAttribute("style", "color: #6b7280; word-break: break-all;");
      sources.textContent = (row.source_keys ?? []).length ? (row.source_keys ?? []).join(" · ") : "—";

      const audioCell = document.createElement("div");
      audioCell.setAttribute("style", "display: flex; flex-direction: column; gap: 4px;");
      audioCell.appendChild(audioStatusBadge(row.audio_status));
      if (row.audio_status !== "missing" && row.audio_url) {
        const player = document.createElement("audio");
        player.controls = true;
        player.preload = "none";
        player.src = row.audio_url;
        player.setAttribute("style", "width: 100%; max-width: 240px;");
        audioCell.appendChild(player);
      }

      const actionsCell = document.createElement("div");
      actionsCell.setAttribute("style", "display: flex; gap: 6px; flex-wrap: wrap;");
      const editBtn = mkBtn("✎ Edit", "outline");
      const rebuildBtn = mkBtn("Rebuild", "success");
      const overrideBtn = mkBtn("Override…", "outline");
      actionsCell.appendChild(editBtn);
      actionsCell.appendChild(rebuildBtn);
      actionsCell.appendChild(overrideBtn);

      // v3.2 (migration 0038): texts have no short_code — display the text_id
      // (truncated) as the row identifier instead.
      const idCell = code(row.text_id.slice(0, 8));
      const tr = t.addRow([idCell, contentCell, sources, audioCell, actionsCell]);

      editBtn.addEventListener("click", () => { openEditPhraseModal(row); });
      rebuildBtn.addEventListener("click", () => { void rebuildOneRow(list.voice.id, row, rebuildBtn); });
      overrideBtn.addEventListener("click", () => {
        toggleOverridePanel(tr, list.voice, row, () => { void loadPhrases(); });
      });
    }
    tableWrap.replaceChildren(t.el);

    // Pager.
    pager.replaceChildren();
    const v = currentBrowserVoice();
    const vid = v ? v.id : "";
    const pageHref = (n: number): string => {
      const q = new URLSearchParams();
      if (vid) q.set("voice", vid);
      if (n > 1) q.set("page", String(n));
      return q.toString() ? `/admin/tts?${q.toString()}` : "/admin/tts";
    };
    if (list.page > 1) pager.appendChild(pagerBtn("‹ Previous", () => navigateAdmin(pageHref(list.page - 1))));
    const info = document.createElement("span");
    info.textContent = `page ${list.page} / ${list.total_pages}`;
    info.setAttribute("style", "color: #6b7280;");
    pager.appendChild(info);
    if (list.page < list.total_pages) pager.appendChild(pagerBtn("Next ›", () => navigateAdmin(pageHref(list.page + 1))));
  }

  async function rebuildOneRow(voiceID: string, row: AdminTTSPhraseDTO, btn: HTMLButtonElement): Promise<void> {
    btn.disabled = true;
    const orig = btn.textContent;
    btn.textContent = "Rebuilding…";
    try {
      await rebuildTextAudio(row.text_id, voiceID, true);
      showAdminToast(main, "Audio rebuilt");
      window.dispatchEvent(new CustomEvent(TTS_COVERAGE_INVALIDATE_EVENT));
      await loadPhrases(); // re-fetch → fresh audio_url with the new ?v=<mtime>
    } catch (err) {
      if (err instanceof AdminError) {
        showAdminToast(main, `Rebuild failed (${err.status}): ${err.bodyText.slice(0, 200)}`, 4000);
      } else {
        showAdminToast(main, errMessage(err, "rebuild audio"), 4000);
      }
      btn.disabled = false;
      btn.textContent = orig ?? "Rebuild";
    }
  }

  // ── Edit phrase content (modal) ──────────────────────────────────────
  //
  // Rewrites every in-use text row sharing this phrase's content. Audio is not
  // auto-rebuilt — the new content gets a fresh SHA, so audio_status flips to
  // "missing" on reload and the existing per-row Rebuild button does the render.

  function openEditPhraseModal(row: AdminTTSPhraseDTO): void {
    const { dialog, content, close } = openModal({ title: "Edit phrase" });

    const form = document.createElement("form");
    form.setAttribute("style", "display: flex; flex-direction: column; gap: 12px;");

    const label = document.createElement("label");
    label.setAttribute("style", "display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #374151;");
    const labelText = document.createElement("span");
    labelText.textContent = "Content";
    const ta = document.createElement("textarea");
    ta.value = row.content;
    ta.rows = 3;
    ta.setAttribute("style", "width: 100%; padding: 8px; border: 1px solid #d1d5db; border-radius: 4px; font-family: inherit; font-size: 14px; resize: vertical; box-sizing: border-box;");
    label.appendChild(labelText);
    label.appendChild(ta);
    form.appendChild(label);

    // Impact preview: every textset slot whose content this save will rewrite.
    const refsWrap = document.createElement("div");
    const refsTitle = document.createElement("div");
    refsTitle.textContent = "Will update these places:";
    refsTitle.setAttribute("style", "font-size: 13px; color: #374151; margin-bottom: 4px;");
    refsWrap.appendChild(refsTitle);
    const refsList = document.createElement("ul");
    refsList.setAttribute("style", "margin: 0; padding-left: 18px; font-size: 12px; color: #6b7280; max-height: 160px; overflow-y: auto;");
    const keys = row.source_keys ?? [];
    if (keys.length === 0) {
      const li = document.createElement("li");
      li.textContent = "—";
      refsList.appendChild(li);
    } else {
      for (const k of keys) {
        const li = document.createElement("li");
        li.textContent = k;
        refsList.appendChild(li);
      }
    }
    refsWrap.appendChild(refsList);
    form.appendChild(refsWrap);

    const hint = document.createElement("p");
    hint.textContent = "Audio is not re-rendered automatically — after save, the row will show audio status “missing” until you click Rebuild.";
    hint.setAttribute("style", "font-size: 12px; color: #6b7280; margin: 0;");
    form.appendChild(hint);

    const errLine = document.createElement("p");
    errLine.setAttribute("style", "color: #991b1b; font-size: 13px; margin: 0; min-height: 1em;");
    form.appendChild(errLine);

    const btnRow = document.createElement("div");
    btnRow.setAttribute("style", "display: flex; gap: 8px; justify-content: flex-end;");
    const cancelBtn = mkBtn("Cancel", "outline");
    cancelBtn.type = "button";
    const saveBtn = mkBtn("Save", "primary");
    saveBtn.type = "submit";
    btnRow.appendChild(cancelBtn);
    btnRow.appendChild(saveBtn);
    form.appendChild(btnRow);

    cancelBtn.addEventListener("click", () => { close(); });

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      errLine.textContent = "";
      const newContent = ta.value.trim();
      if (newContent === "") { errLine.textContent = "Content must not be empty."; return; }
      if (newContent === row.content) { errLine.textContent = "Content is unchanged."; return; }
      const targets = row.text_ids && row.text_ids.length > 0 ? row.text_ids : [row.text_id];
      if (targets.length === 0) { errLine.textContent = "No text rows to update."; return; }
      void (async () => {
        saveBtn.disabled = true;
        cancelBtn.disabled = true;
        try {
          await updatePhraseContent(targets, newContent);
          showAdminToast(main, "Phrase updated");
          window.dispatchEvent(new CustomEvent(TTS_COVERAGE_INVALIDATE_EVENT));
          close();
          await loadPhrases();
        } catch (err) {
          if (err instanceof AdminError) errLine.textContent = `${err.status}: ${err.bodyText.slice(0, 200)}`;
          else errLine.textContent = errMessage(err, "update phrase");
          saveBtn.disabled = false;
          cancelBtn.disabled = false;
        }
      })();
    });

    content.appendChild(form);
    // Focus + select for fast typing-over.
    queueMicrotask(() => { ta.focus(); ta.select(); });
    void dialog;
  }

  // ── Override panel (inserted <tr> below the phrase row) ──────────────

  function toggleOverridePanel(rowTr: HTMLTableRowElement, voice: AdminVoiceDTO, row: AdminTTSPhraseDTO, onChanged: () => void): void {
    const next = rowTr.nextElementSibling as HTMLTableRowElement | null;
    if (next && next.classList.contains("tts-override-row")) { next.remove(); return; }
    const ovTr = document.createElement("tr");
    ovTr.className = "tts-override-row";
    const ovTd = document.createElement("td");
    ovTd.colSpan = PHRASE_COLS;
    ovTd.setAttribute("style", "background: #f9fafb; padding: 12px;");
    ovTd.appendChild(buildOverrideForm(voice, row, () => { ovTr.remove(); onChanged(); }));
    ovTr.appendChild(ovTd);
    rowTr.after(ovTr);
  }

  function buildOverrideForm(voice: AdminVoiceDTO, row: AdminTTSPhraseDTO, onClose: () => void): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("style", "display: flex; flex-direction: column; gap: 8px; max-width: 640px;");
    const title = document.createElement("strong");
    title.textContent = `Override for ${voice.label} — “${row.content}”`;
    title.setAttribute("style", "font-size: 13px;");
    wrap.appendChild(title);

    const isGoogle = voice.provider === "google";
    let promptTa: LabeledField<HTMLTextAreaElement> | null = null;
    let rateInput: LabeledField<HTMLInputElement> | null = null;
    let pitchInput: LabeledField<HTMLInputElement> | null = null;
    let instrTa: LabeledField<HTMLTextAreaElement> | null = null;

    if (isGoogle) {
      promptTa = mkTextarea("Steering prompt (blank = voice default)", row.override?.prompt ?? "");
      rateInput = mkNumber("Speaking rate 0.25–4.0 (blank = 1.0)", row.override?.speaking_rate, 0.05, 0.25, 4);
      pitchInput = mkNumber("Pitch −20…20 (blank = 0)", row.override?.pitch, 0.5, -20, 20);
      wrap.appendChild(promptTa.label);
      wrap.appendChild(rateInput.label);
      wrap.appendChild(pitchInput.label);
    } else {
      instrTa = mkTextarea("Steering instructions (blank = voice default)", row.override?.instructions ?? "");
      wrap.appendChild(instrTa.label);
    }

    const errLine = document.createElement("p");
    errLine.setAttribute("style", "color: #991b1b; font-size: 13px; margin: 0; min-height: 1em;");
    wrap.appendChild(errLine);

    const btnRow = document.createElement("div");
    btnRow.setAttribute("style", "display: flex; gap: 8px; flex-wrap: wrap;");
    const saveBtn = mkBtn("Save override", "primary");
    const resetBtn = mkBtn("Reset to defaults", "danger");
    const closeBtn = mkBtn("Close", "outline");
    btnRow.appendChild(saveBtn);
    btnRow.appendChild(resetBtn);
    btnRow.appendChild(closeBtn);
    wrap.appendChild(btnRow);

    const rebuildPrompt = document.createElement("div");
    rebuildPrompt.style.display = "none";
    rebuildPrompt.setAttribute("style", "display: none; gap: 8px; align-items: center; font-size: 13px;");
    const rebuildPromptText = document.createElement("span");
    const rebuildNowBtn = mkBtn("Rebuild now", "success");
    rebuildPrompt.appendChild(rebuildPromptText);
    rebuildPrompt.appendChild(rebuildNowBtn);
    wrap.appendChild(rebuildPrompt);

    const showRebuildPrompt = (msg: string): void => {
      rebuildPromptText.textContent = msg;
      rebuildPrompt.style.display = "flex";
    };
    rebuildNowBtn.addEventListener("click", () => {
      void (async () => {
        rebuildNowBtn.disabled = true;
        try {
          await rebuildTextAudio(row.text_id, voice.id, true);
          showAdminToast(main, "Audio rebuilt");
          onClose(); // collapse + refresh the list (picks up the new ?v=)
        } catch (err) {
          errLine.textContent = errMessage(err, "rebuild audio");
        } finally { rebuildNowBtn.disabled = false; }
      })();
    });

    closeBtn.addEventListener("click", onClose);

    saveBtn.addEventListener("click", () => {
      errLine.textContent = "";
      const body: AdminPhraseOverrideUpsertDTO = { voice: voice.id, text: row.content };
      if (isGoogle) {
        if (promptTa && promptTa.input.value.trim() !== "") body.prompt = promptTa.input.value;
        if (rateInput && rateInput.input.value.trim() !== "") {
          const n = Number(rateInput.input.value);
          if (!Number.isFinite(n) || n < 0.25 || n > 4) { errLine.textContent = "speaking rate must be 0.25–4.0"; return; }
          body.speaking_rate = n;
        }
        if (pitchInput && pitchInput.input.value.trim() !== "") {
          const n = Number(pitchInput.input.value);
          if (!Number.isFinite(n) || n < -20 || n > 20) { errLine.textContent = "pitch must be −20…20"; return; }
          body.pitch = n;
        }
      } else if (instrTa && instrTa.input.value.trim() !== "") {
        body.instructions = instrTa.input.value;
      }
      void (async () => {
        saveBtn.disabled = true;
        try {
          await setTTSOverride(body);
          row.has_override = body.prompt != null || body.speaking_rate != null || body.pitch != null || body.instructions != null;
          showAdminToast(main, row.has_override ? "Override saved" : "Override removed");
          showRebuildPrompt(row.has_override ? "Override saved — rebuild this phrase to hear it?" : "Override removed — rebuild this phrase to revert audio?");
        } catch (err) {
          if (err instanceof AdminError) errLine.textContent = `${err.status}: ${err.bodyText.slice(0, 200)}`;
          else errLine.textContent = errMessage(err, "save override");
        } finally { saveBtn.disabled = false; }
      })();
    });

    resetBtn.addEventListener("click", () => {
      errLine.textContent = "";
      void (async () => {
        resetBtn.disabled = true;
        try {
          await deleteTTSOverride(voice.id, row.content);
          row.has_override = false;
          if (promptTa) promptTa.input.value = "";
          if (rateInput) rateInput.input.value = "";
          if (pitchInput) pitchInput.input.value = "";
          if (instrTa) instrTa.input.value = "";
          showAdminToast(main, "Override removed");
          showRebuildPrompt("Override removed — rebuild this phrase to revert audio?");
        } catch (err) {
          errLine.textContent = errMessage(err, "reset override");
        } finally { resetBtn.disabled = false; }
      })();
    });

    return wrap;
  }

  // ── Bulk rebuild ─────────────────────────────────────────────────────

  function doBulk(mode: "missing" | "force", scope: "voice" | "all"): void {
    const v = currentBrowserVoice();
    if (!v) return;
    const nonDeletedCount = voices.filter((x) => !x.deleted).length;
    const activeBtn = mode === "force" ? rebuildAllBtn : rebuildMissingBtn;
    const idleBtn = mode === "force" ? rebuildMissingBtn : rebuildAllBtn;
    const activeIdleLabel = mode === "force" ? rebuildAllIdleLabel : rebuildMissingIdleLabel;
    const activeLoadingLabel = mode === "force" ? "Rebuilding…" : "Building…";
    setBtnLoading(activeBtn, true, activeIdleLabel, activeLoadingLabel);
    idleBtn.disabled = true;
    const target = scope === "all" ? `all ${nonDeletedCount} voices` : v.label;
    bulkNote.textContent = mode === "force"
      ? `Rebuilding all for ${target}… (this may take a while — don’t close this tab)`
      : `Building missing for ${target}…`;
    bulkResult.replaceChildren();
    void (async () => {
      try {
        const res = await rebuildTTSBulk(v.id, mode, scope);
        const p = document.createElement("p");
        p.setAttribute("style", "font-size: 14px; margin: 0;");
        p.textContent = `Rendered ${res.rendered} · skipped ${res.skipped}${res.ok ? "" : " · FAILED"}`;
        bulkResult.appendChild(p);
        if (!res.ok && res.error) {
          const e = document.createElement("p");
          e.setAttribute("style", "color: #991b1b; font-size: 13px; margin: 4px 0 0 0;");
          e.textContent = res.error;
          bulkResult.appendChild(e);
        }
        if (res.voices && res.voices.length > 0) {
          const ul = document.createElement("ul");
          ul.setAttribute("style", "margin: 6px 0 0 0; padding-left: 20px; font-size: 13px;");
          for (const vr of res.voices) {
            const li = document.createElement("li");
            const tail = vr.ok ? "" : ` — FAILED: ${vr.error}`;
            li.textContent = `${vr.voice_label}: rendered ${vr.rendered} · skipped ${vr.skipped}${tail}`;
            if (!vr.ok) li.style.color = "#991b1b";
            ul.appendChild(li);
          }
          bulkResult.appendChild(ul);
        }
        showAdminToast(main, `Done — ${res.rendered} rendered${res.ok ? "" : ", with errors"}`);
        window.dispatchEvent(new CustomEvent(TTS_COVERAGE_INVALIDATE_EVENT));
        await loadPhrases();
        await loadSettingsAndCatalog(); // a force rebuild may flip a voice's "rendered" flag
      } catch (err) {
        const e = document.createElement("p");
        e.setAttribute("style", "color: #991b1b; font-size: 13px; margin: 0;");
        e.textContent = errMessage(err, "rebuild phrases");
        bulkResult.appendChild(e);
      } finally {
        setBtnLoading(activeBtn, false, activeIdleLabel, activeLoadingLabel);
        idleBtn.disabled = false;
        bulkNote.textContent = "";
      }
    })();
  }

  // askScopeThenBulk opens a modal asking the operator whether to rebuild for
  // just the currently selected voice or for every non-deleted voice in the
  // catalogue. For "force" mode the modal also doubles as the safety
  // confirmation (no separate window.confirm fires) — the body text spells
  // out the cost so the choice is the consent.
  function askScopeThenBulk(mode: "missing" | "force"): void {
    const v = currentBrowserVoice();
    if (!v) return;
    const nonDeleted = voices.filter((x) => !x.deleted);
    const { content, close } = openModal({
      title: mode === "force" ? "Rebuild all phrases" : "Build missing phrases",
    });

    const p = document.createElement("p");
    p.setAttribute("style", "margin: 0 0 12px 0; font-size: 14px;");
    p.textContent = mode === "force"
      ? "This re-renders ALL in-use phrases and makes a TTS API call per phrase. It may take a few minutes. Choose scope:"
      : "Render any in-use phrases that don't yet have a cached audio file. Choose scope:";
    content.appendChild(p);

    const btnRow = document.createElement("div");
    btnRow.setAttribute("style", "display: flex; gap: 8px; flex-wrap: wrap;");
    const justThis = mkBtn(`Just this voice (${v.label})`, "primary");
    const allBtn = mkBtn(`All voices (${nonDeleted.length})`, "outline");
    btnRow.append(justThis, allBtn);
    content.appendChild(btnRow);

    justThis.addEventListener("click", () => { close(); doBulk(mode, "voice"); });
    allBtn.addEventListener("click", () => { close(); doBulk(mode, "all"); });
  }

  rebuildMissingBtn.addEventListener("click", () => askScopeThenBulk("missing"));
  rebuildAllBtn.addEventListener("click", () => askScopeThenBulk("force"));

  // ── Catalogue actions ────────────────────────────────────────────────

  catSel.addEventListener("change", () => {
    // Navigate so the URL reflects the inspected voice (Back-friendly).
    navigateAdmin(`/admin/tts?voice=${encodeURIComponent(catSel.value)}`);
  });
  setActiveBtn.addEventListener("click", () => {
    const v = voices.find((x) => x.id === catSel.value);
    if (!v) return;
    void (async () => {
      setActiveBtn.disabled = true;
      try {
        const updated = await setTTSChoice(v.provider, v.voice);
        cfg = updated;
        renderedByTuple.clear();
        for (const o of updated.options) renderedByTuple.set(tupleKey(o.provider, o.voice), o.available);
        showAdminToast(main, `Active voice → ${v.label}`);
        await loadSettingsAndCatalog();
      } catch (err) {
        if (err instanceof TTSConfigError && err.code === "voice_not_rendered") {
          setCatStatus("That voice isn’t pre-rendered yet — use “Rebuild all” for it first.", true);
        } else {
          setCatStatus(errMessage(err, "set active voice"), true);
        }
        setActiveBtn.disabled = false;
      }
    })();
  });
  deleteVoiceBtn.addEventListener("click", () => {
    const v = voices.find((x) => x.id === catSel.value);
    if (!v) return;
    if (!window.confirm(`Delete voice “${v.label}”? (Soft delete — audio files stay on disk; the cost log keeps its rows.)`)) return;
    void (async () => {
      deleteVoiceBtn.disabled = true;
      try {
        await deleteTTSVoice(v.id);
        showAdminToast(main, "Voice deleted");
        navigateAdmin("/admin/tts"); // reset to the default voice
      } catch (err) {
        setCatStatus(errMessage(err, "delete voice"), true);
        deleteVoiceBtn.disabled = false;
      }
    })();
  });
  addVoiceBtn.addEventListener("click", () => {
    if (addVoiceForm.style.display !== "none") { addVoiceForm.style.display = "none"; return; }
    addVoiceForm.replaceChildren(buildAddVoiceForm());
    addVoiceForm.style.display = "block";
  });

  function buildAddVoiceForm(): HTMLElement {
    const wrap = document.createElement("div");
    wrap.setAttribute("style", "margin-top: 12px; display: flex; flex-direction: column; gap: 8px; max-width: 480px; border: 1px dashed #d1d5db; border-radius: 6px; padding: 12px;");
    const label = mkTextInput("Label", "");
    const providerSel = document.createElement("select");
    providerSel.setAttribute("style", "font-size: 13px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px;");
    for (const p of ["openai", "google"]) { const o = document.createElement("option"); o.value = p; o.textContent = p; providerSel.appendChild(o); }
    const providerLabel = document.createElement("label");
    providerLabel.setAttribute("style", "display: block; font-size: 13px; color: #374151;");
    providerLabel.textContent = "Provider: ";
    providerLabel.appendChild(providerSel);
    const voiceId = mkTextInput("Voice id (e.g. alloy / Achernar)", "");
    const model = mkTextInput("Google model (required for google)", "");
    const prompt = mkTextarea("Google steering prompt (optional)", "");
    const sexSel = document.createElement("select");
    sexSel.setAttribute("style", "font-size: 13px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px;");
    for (const s of ["neutral", "female", "male"]) { const o = document.createElement("option"); o.value = s; o.textContent = s; sexSel.appendChild(o); }
    const sexLabel = document.createElement("label");
    sexLabel.setAttribute("style", "display: block; font-size: 13px; color: #374151;");
    sexLabel.textContent = "Sex (label only): ";
    sexLabel.appendChild(sexSel);

    const googleOnly = [model.label, prompt.label];
    const syncGoogleVis = (): void => { for (const el of googleOnly) el.style.display = providerSel.value === "google" ? "block" : "none"; };
    providerSel.addEventListener("change", syncGoogleVis);
    syncGoogleVis();

    const errLine = document.createElement("p");
    errLine.setAttribute("style", "color: #991b1b; font-size: 13px; margin: 0; min-height: 1em;");
    const createBtn = mkBtn("Create voice", "primary");
    createBtn.addEventListener("click", () => {
      errLine.textContent = "";
      if (label.input.value.trim() === "" || voiceId.input.value.trim() === "") { errLine.textContent = "label and voice id are required"; return; }
      if (providerSel.value === "google" && model.input.value.trim() === "") { errLine.textContent = "model is required for google voices"; return; }
      void (async () => {
        createBtn.disabled = true;
        try {
          const created = await createTTSVoice({
            label: label.input.value, provider: providerSel.value, voice: voiceId.input.value,
            model: providerSel.value === "google" ? model.input.value : "",
            prompt: providerSel.value === "google" ? prompt.input.value : "",
            sex: sexSel.value,
          });
          showAdminToast(main, "Voice created");
          navigateAdmin(`/admin/tts?voice=${encodeURIComponent(created.id)}`);
        } catch (err) {
          if (err instanceof AdminError) errLine.textContent = `${err.status}: ${err.bodyText.slice(0, 200)}`;
          else errLine.textContent = errMessage(err, "create voice");
          createBtn.disabled = false;
        }
      })();
    });

    wrap.appendChild(label.label);
    wrap.appendChild(providerLabel);
    wrap.appendChild(voiceId.label);
    wrap.appendChild(model.label);
    wrap.appendChild(prompt.label);
    wrap.appendChild(sexLabel);
    wrap.appendChild(errLine);
    wrap.appendChild(createBtn);
    return wrap;
  }

  // Active-voice <select> change → setTTSChoice.
  activeSel.addEventListener("change", () => {
    const [provider, voice] = activeSel.value.split("|");
    if (!provider || !voice) return;
    void (async () => {
      setSettingsStatus("Saving…");
      try {
        const updated = await setTTSChoice(provider, voice);
        cfg = updated;
        renderedByTuple.clear();
        for (const o of updated.options) renderedByTuple.set(tupleKey(o.provider, o.voice), o.available);
        renderActiveSelect();
        renderSteering();
        setSettingsStatus("Active voice changed.");
        await loadSettingsAndCatalog();
      } catch (err) {
        if (err instanceof TTSConfigError && err.code === "voice_not_rendered") {
          setSettingsStatus("That voice isn’t pre-rendered yet. Use “Rebuild all” for it, then retry.", true);
          void loadSettingsAndCatalog();
        } else {
          setSettingsStatus(errMessage(err, "set active voice"), true);
        }
      }
    })();
  });

  // ── Boot ─────────────────────────────────────────────────────────────
  void (async () => {
    await loadSettingsAndCatalog();
    await loadPhrases();
  })();

  return () => {};
}

// ── Small DOM helpers (local to this screen) ─────────────────────────────

function mkBtn(label: string, kind: "primary" | "outline" | "danger" | "success"): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = label;
  const base = "padding: 6px 12px; border-radius: 4px; cursor: pointer; font-size: 13px; border: 1px solid #d1d5db;";
  const byKind: Record<typeof kind, string> = {
    primary: "background: #2563eb; color: white; border-color: #2563eb;",
    outline: "background: white; color: #1d4ed8;",
    danger: "background: white; color: #b91c1c; border-color: #fca5a5;",
    success: "background: #16a34a; color: white; border-color: #16a34a;",
  };
  b.setAttribute("style", base + byKind[kind]);
  return b;
}

function spinnerSVG(): SVGSVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("width", "12");
  svg.setAttribute("height", "12");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("style", "vertical-align: -1px; margin-right: 6px;");
  const bg = document.createElementNS(NS, "circle");
  bg.setAttribute("cx", "12"); bg.setAttribute("cy", "12"); bg.setAttribute("r", "10");
  bg.setAttribute("stroke", "currentColor"); bg.setAttribute("stroke-width", "3");
  bg.setAttribute("stroke-opacity", "0.25"); bg.setAttribute("fill", "none");
  svg.appendChild(bg);
  const arc = document.createElementNS(NS, "path");
  arc.setAttribute("d", "M22 12a10 10 0 0 1-10 10");
  arc.setAttribute("stroke", "currentColor"); arc.setAttribute("stroke-width", "3");
  arc.setAttribute("stroke-linecap", "round"); arc.setAttribute("fill", "none");
  const anim = document.createElementNS(NS, "animateTransform");
  anim.setAttribute("attributeName", "transform");
  anim.setAttribute("type", "rotate");
  anim.setAttribute("from", "0 12 12");
  anim.setAttribute("to", "360 12 12");
  anim.setAttribute("dur", "0.9s");
  anim.setAttribute("repeatCount", "indefinite");
  arc.appendChild(anim);
  svg.appendChild(arc);
  return svg;
}

function setBtnLoading(btn: HTMLButtonElement, loading: boolean, idleLabel: string, loadingLabel: string): void {
  btn.disabled = loading;
  btn.replaceChildren();
  if (loading) {
    btn.appendChild(spinnerSVG());
    btn.appendChild(document.createTextNode(loadingLabel));
  } else {
    btn.appendChild(document.createTextNode(idleLabel));
  }
}

function pagerBtn(label: string, onClick: () => void): HTMLButtonElement {
  const b = mkBtn(label, "outline");
  b.addEventListener("click", onClick);
  return b;
}

function audioStatusBadge(status: string): HTMLSpanElement {
  if (status === "exists") return pill("✓ rendered", "#166534", "#dcfce7");
  if (status === "stale") return pill("⚠ stale", "#92400e", "#fef3c7");
  return pill("✗ missing", "#991b1b", "#fee2e2");
}

function mkTextInput(labelText: string, value: string): LabeledField<HTMLInputElement> {
  const label = document.createElement("label");
  label.setAttribute("style", "display: block; font-size: 13px; color: #374151;");
  label.textContent = `${labelText}: `;
  const input = document.createElement("input");
  input.type = "text";
  input.value = value;
  input.setAttribute("style", "width: 100%; font-size: 13px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; box-sizing: border-box; margin-top: 2px;");
  label.appendChild(input);
  return { label, input };
}

function mkTextarea(labelText: string, value: string): LabeledField<HTMLTextAreaElement> {
  const label = document.createElement("label");
  label.setAttribute("style", "display: block; font-size: 13px; color: #374151;");
  label.textContent = `${labelText}: `;
  const input = document.createElement("textarea");
  input.rows = 3;
  input.maxLength = 2000;
  input.value = value;
  input.setAttribute("style", "width: 100%; font-size: 13px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; box-sizing: border-box; font-family: inherit; margin-top: 2px;");
  label.appendChild(input);
  return { label, input };
}

function mkNumber(labelText: string, value: number | null | undefined, step: number, min: number, max: number): LabeledField<HTMLInputElement> {
  const label = document.createElement("label");
  label.setAttribute("style", "display: block; font-size: 13px; color: #374151;");
  label.textContent = `${labelText}: `;
  const input = document.createElement("input");
  input.type = "number";
  input.step = String(step);
  input.min = String(min);
  input.max = String(max);
  input.value = value == null ? "" : String(value);
  input.setAttribute("style", "width: 140px; font-size: 13px; padding: 6px 8px; border: 1px solid #d1d5db; border-radius: 4px; margin-top: 2px;");
  label.appendChild(input);
  return { label, input };
}
